import React, { useState } from 'react';
import {
  getMediaMimeType,
  isMediaImage,
  isMediaVideo,
} from '../../utils/mediaDetect';
import PhotoLightbox from '../PhotoLightbox';

const GAP = 2;
const RADIUS = 8;
const SINGLE_VIDEO_MAX_HEIGHT = 540;

const isAudio = (url) => /\.(mp3|wav|ogg)$/i.test(String(url || ''));
const isDocument = (url) => /\.(pdf|docx?|pptx?|xlsx?)$/i.test(String(url || ''));

function mediaTypeOf(item) {
  return item?.media_type || item?.type;
}

/**
 * Full-width playable video (no crop). Download control and save-as are disabled.
 */
function FitVideo({ url, mime, maxHeight = SINGLE_VIDEO_MAX_HEIGHT }) {
  return (
    <video
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload"
      disablePictureInPicture
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'block',
        width: '100%',
        maxHeight,
        minHeight: 280,
        objectFit: 'contain',
        background: '#000',
      }}
    >
      <source src={url} type={getMediaMimeType(url, mime)} />
    </video>
  );
}

function MediaCell({ item, onOpen, overlay, style }) {
  const url = item?.url;
  if (!url) return null;

  const mime = mediaTypeOf(item);
  const img = isMediaImage(url, mime);
  const vid = isMediaVideo(url, mime);

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: '#000',
        minWidth: 0,
        minHeight: 0,
        cursor: img || vid ? 'pointer' : 'default',
        ...style,
      }}
      onClick={() => (img || vid) && onOpen?.(item)}
    >
      {img ? (
        <img
          src={url}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : vid ? (
        <>
          <video
            playsInline
            muted
            preload="metadata"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              background: '#000',
              pointerEvents: 'none',
            }}
          >
            <source src={url} type={getMediaMimeType(url, mime)} />
          </video>
          <span
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 36,
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
              pointerEvents: 'none',
            }}
          >
            ▶
          </span>
        </>
      ) : null}

      {overlay}
    </div>
  );
}

function PlusOverlay({ count, onClick }) {
  if (!count || count <= 0) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
        }
      }}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        color: '#fff',
        fontSize: 28,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        letterSpacing: 0.5,
      }}
    >
      +{count}
    </div>
  );
}

/**
 * Facebook-style photo/video collage for post feeds.
 *
 * 1  → full-width
 * 2  → equal side-by-side
 * 3  → large first (left), two stacked (right)
 * 4  → 2×2
 * 5+ → 2 on top + 3 on bottom; last tile shows +N when more remain
 *
 * Clicking a photo opens a swipeable gallery lightbox.
 * A single video plays inline at full post width (not cropped).
 */
export default function PostMediaGrid({
  media,
  expanded: expandedProp,
  onToggleExpand,
  style,
}) {
  const [expandedLocal, setExpandedLocal] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { index }
  const expanded = expandedProp ?? expandedLocal;

  const items = Array.isArray(media) ? media.filter((m) => m?.url) : [];
  const galleryItems = items.filter((item) => {
    const mime = mediaTypeOf(item);
    return isMediaImage(item.url, mime) || isMediaVideo(item.url, mime);
  });

  const toggleExpand = () => {
    if (onToggleExpand) onToggleExpand();
    else setExpandedLocal((v) => !v);
  };

  const openAt = (item) => {
    const idx = galleryItems.findIndex((g) => g.url === item?.url);
    setLightbox({ index: idx >= 0 ? idx : 0 });
  };

  const openAtIndex = (index) => {
    setLightbox({ index: Math.max(0, Math.min(index, galleryItems.length - 1)) });
  };

  if (items.length === 0) return null;

  const count = items.length;

  const wrap = (children, extra = {}) => (
    <div
      style={{
        marginTop: 12,
        width: '100%',
        borderRadius: RADIUS,
        overflow: 'hidden',
        background: '#f0f2f5',
        ...extra,
        ...style,
      }}
    >
      {children}
      {lightbox && (
        <PhotoLightbox
          items={galleryItems}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );

  /* ---- 1 media ---- */
  if (count === 1) {
    const item = items[0];
    const mime = mediaTypeOf(item);
    const img = isMediaImage(item.url, mime);
    const vid = isMediaVideo(item.url, mime);

    if (isAudio(item.url)) {
      return wrap(
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: '#f9f9f9',
          }}
        >
          <audio controls style={{ width: '90%', height: 50 }}>
            <source src={item.url} type="audio/mpeg" />
          </audio>
        </div>
      );
    }

    if (isDocument(item.url)) {
      return wrap(
        <iframe
          src={item.url}
          title="Document Preview"
          style={{ width: '100%', height: 500, border: 'none', display: 'block' }}
        />
      );
    }

    if (!img && !vid) {
      return wrap(
        <p style={{ color: '#999', fontStyle: 'italic', padding: 16, margin: 0 }}>
          Unsupported media type
        </p>
      );
    }

    if (vid) {
      return wrap(
        <FitVideo url={item.url} mime={mime} />,
        { background: '#000' }
      );
    }

    return wrap(
      <div
        style={{
          position: 'relative',
          width: '100%',
          background: '#f0f2f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={() => openAt(item)}
      >
        <img
          src={item.url}
          alt=""
          style={{
            width: '100%',
            maxHeight: 540,
            objectFit: 'contain',
            display: 'block',
            background: '#f0f2f5',
          }}
        />
      </div>
    );
  }

  /* ---- 2 media ---- */
  if (count === 2) {
    return wrap(
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: GAP,
          height: 380,
        }}
      >
        {items.map((item, idx) => (
          <MediaCell key={idx} item={item} onOpen={openAt} />
        ))}
      </div>
    );
  }

  /* ---- 3 media: large first on left, two stacked right (Facebook) ---- */
  if (count === 3) {
    return wrap(
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: `
            "main sideTop"
            "main sideBot"
          `,
          gap: GAP,
          height: 460,
        }}
      >
        <MediaCell item={items[0]} onOpen={openAt} style={{ gridArea: 'main' }} />
        <MediaCell item={items[1]} onOpen={openAt} style={{ gridArea: 'sideTop' }} />
        <MediaCell item={items[2]} onOpen={openAt} style={{ gridArea: 'sideBot' }} />
      </div>
    );
  }

  /* ---- 4 media: 2×2 ---- */
  if (count === 4) {
    return wrap(
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: GAP,
          height: 420,
        }}
      >
        {items.map((item, idx) => (
          <MediaCell key={idx} item={item} onOpen={openAt} />
        ))}
      </div>
    );
  }

  /* ---- 5+ media ---- */
  const remaining = count - 5;
  const showAll = expanded;

  if (showAll) {
    return wrap(
      <>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: GAP,
          }}
        >
          {items.map((item, idx) => (
            <MediaCell
              key={idx}
              item={item}
              onOpen={openAt}
              style={{ aspectRatio: '1 / 1' }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={toggleExpand}
          style={{
            display: 'block',
            width: '100%',
            margin: 0,
            padding: '10px 0',
            background: '#f0f2f5',
            border: 'none',
            borderTop: '1px solid #e4e6eb',
            color: '#050505',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Show less
        </button>
      </>
    );
  }

  // Facebook: 2 equal on top, 3 equal on bottom; +N on last when more than 5
  return wrap(
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gridTemplateRows: '1.15fr 1fr',
        gridTemplateAreas: `
          "a a a b b b"
          "c c d d e e"
        `,
        gap: GAP,
        height: 460,
      }}
    >
      <MediaCell item={items[0]} onOpen={openAt} style={{ gridArea: 'a' }} />
      <MediaCell item={items[1]} onOpen={openAt} style={{ gridArea: 'b' }} />
      <MediaCell item={items[2]} onOpen={openAt} style={{ gridArea: 'c' }} />
      <MediaCell item={items[3]} onOpen={openAt} style={{ gridArea: 'd' }} />
      <MediaCell
        item={items[4]}
        onOpen={openAt}
        style={{ gridArea: 'e' }}
        overlay={
          <PlusOverlay
            count={remaining}
            onClick={() => openAtIndex(4)}
          />
        }
      />
    </div>
  );
}
